import React from "react";
import environment from "web/script/modules/environment";
import CloseButton from "web/react/components/buttons/close-button/close-button";
import BranchLink from "web/react/components/branch-link/branch-link";
import { gettext } from "web/script/modules/django-i18n";
import classnames from "classnames";
import { BranchBannerCampaign } from "web/script/utils/app-banner-campaign";
import { isExcludedDesignerOnPage } from "web/react/components/webviews/financial-incentive-webviews/financial-incentive-utils";
import { FinancialIncentivesCampaignRawBranchLink } from "web/script/utils/branch-io";
import styles from "./small-app-download-banner.css";

interface SmallAppDownloadBannerProps {
    bannerCampaign: BranchBannerCampaign;
    onClose: () => void;
}
interface FinancialIncentivesBannerProps {
    branchLink: string;
    rewardValue: string;
}

function getFinancialIncentiveBannerProps(): FinancialIncentivesBannerProps | null {
    const pageType = environment.get("pageType");
    const pageSubType = environment.get("pageSubType");
    let featureFlag = "";
    let branchLink = "";
    if (pageType == "homepage") {
        featureFlag = "mg_financial_incentives_homepage";
        branchLink = FinancialIncentivesCampaignRawBranchLink.Homepage;
    } else if (pageType == "feed") {
        featureFlag = "mg_financial_incentives_feed";
        branchLink = FinancialIncentivesCampaignRawBranchLink.Feed;
    } else if (pageType == "product" && pageSubType == "in_stock" && !isExcludedDesignerOnPage()) {
        featureFlag = "mg_financial_incentives_is_pdp";
        branchLink = FinancialIncentivesCampaignRawBranchLink.ISPDP;
    }
    if (featureFlag !== "" && environment.getFeature(featureFlag) !== "") {
        return {
            branchLink,
            rewardValue: environment.getFeature(`${featureFlag}_value`).toString(),
        };
    }
    return null;
}

export function SmallAppDownloadBanner({
    bannerCampaign,
    onClose,
}: SmallAppDownloadBannerProps): JSX.Element {
    // Open
    const ctaText = gettext("app_download_small_banner.general.cta");
    let bannerTitle;
    let bannerDescription;
    let isLargeDescription = false;
    let branchLink;
    const financialIncentivesBannerProps = getFinancialIncentiveBannerProps();
    if (financialIncentivesBannerProps && financialIncentivesBannerProps.rewardValue !== "") {
        // MG-226 - Financial Incentive Banners
        // "Get {reward_value} Back"
        bannerTitle = gettext("financial_incentive.app_download_small_banner.title", {
            reward_value: financialIncentivesBannerProps.rewardValue,
        });
        // Download the Lyst App and get {reward_value} back as a reward
        bannerDescription = gettext("financial_incentive.app_download_small_banner.description", {
            reward_value: financialIncentivesBannerProps.rewardValue,
        });
        isLargeDescription = true;
        branchLink = financialIncentivesBannerProps.branchLink;
    } else {
        // "Get the Lyst App"
        bannerTitle = gettext("app_download_small_banner.general.title");
        bannerDescription = gettext("app_download_small_banner.better_in_the_app.description");
        branchLink = bannerCampaign.branchLink;
    }

    return (
        <>
            <div className={styles.placeholder} />
            <div className={classnames(styles.sticky, styles.downloadBanner)}>
                <div className={styles.wrapper}>
                    <CloseButton className={styles.closeButton} onClick={onClose} />
                    <div className={styles.appImage}>
                        <img src={bannerCampaign.bannerImageUrl} alt={"Lyst App"} />
                    </div>
                    <div
                        className={classnames(styles.content, {
                            [styles.largeContent]: isLargeDescription,
                        })}
                    >
                        <div className={styles.title}>{bannerTitle}</div>
                        <div className={styles.description}>{bannerDescription}</div>
                    </div>
                    <div className={styles.ctaContainer}>
                        <BranchLink
                            branchLink={branchLink}
                            className={styles.cta}
                            eventLabel={`app_banner_${bannerCampaign.name}`}
                            title={ctaText}
                            primaryButton
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

export default SmallAppDownloadBanner;
